import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

/**
 * Shows a message with `notify(message, type, displayTime)` in the Material
 * snack bar at the bottom center of the window, colored by the type.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private snackBar = inject(MatSnackBar);

  notify(message: string, type: NotificationType, displayTime: number) {
    this.snackBar.open(message, undefined, {
      duration: displayTime,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['app-notification', `app-notification-${type}`],
    });
  }

  /**
   * Like notify, and returns a handle that closes the message early, e.g. a
   * warning that no longer applies.
   */
  notifyDismissible(
    message: string,
    type: NotificationType,
    displayTime: number
  ): { dismiss(): void } {
    const ref = this.snackBar.open(message, undefined, {
      duration: displayTime,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['app-notification', `app-notification-${type}`],
    });
    return { dismiss: () => ref.dismiss() };
  }

  /**
   * A success message with an undo button for 6 s; the observable emits
   * once when the user clicks it.
   */
  notifyWithUndo(message: string, undoLabel: string): Observable<void> {
    return this.snackBar
      .open(message, undoLabel, {
        duration: 6000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        politeness: 'polite',
        panelClass: ['app-notification', 'app-notification-success'],
      })
      .onAction();
  }
}
